import { MAP_STYLE_WHITE } from "../../../components/map/mapStyles";
import type { Geofence } from "../../../store/admin/geofencesStore";
import type { Route } from "../../../store/admin/routesStore";
import { GEOFENCE_TYPE_MAP_COLORS, WATCH_STATUS_WEIGHT } from "../theme";
import type { WatchStatus } from "../types";

export type LatLngLiteral = { lat: number; lng: number };

export const TACTICAL_MAP_STYLE_DARK: google.maps.MapTypeStyle[] = [
  { elementType: "geometry", stylers: [{ color: "#0b1017" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#6b7f97" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#05070b" }, { weight: 3 }] },
  { featureType: "administrative", elementType: "geometry.stroke", stylers: [{ color: "#243447" }] },
  { featureType: "administrative.country", elementType: "geometry.stroke", stylers: [{ color: "#33475f" }] },
  { featureType: "administrative.land_parcel", stylers: [{ visibility: "off" }] },
  { featureType: "administrative.neighborhood", stylers: [{ visibility: "off" }] },
  { featureType: "administrative.locality", elementType: "labels.text.fill", stylers: [{ color: "#8ea3bd" }] },
  { featureType: "poi", stylers: [{ visibility: "off" }] },
  { featureType: "landscape.natural", elementType: "geometry", stylers: [{ color: "#0d131b" }] },
  { featureType: "road", elementType: "geometry.fill", stylers: [{ color: "#182231" }] },
  { featureType: "road", elementType: "geometry.stroke", stylers: [{ color: "#101821" }] },
  { featureType: "road", elementType: "labels.text.fill", stylers: [{ color: "#5f7a94" }] },
  { featureType: "road.arterial", elementType: "geometry.fill", stylers: [{ color: "#1c2836" }] },
  { featureType: "road.highway", elementType: "geometry.fill", stylers: [{ color: "#28394d" }] },
  { featureType: "road.highway", elementType: "geometry.stroke", stylers: [{ color: "#16202c" }] },
  { featureType: "road.highway", elementType: "labels.text.fill", stylers: [{ color: "#8399b4" }] },
  { featureType: "road.local", elementType: "labels", stylers: [{ visibility: "off" }] },
  { featureType: "transit", stylers: [{ visibility: "off" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#03060a" }] },
  { featureType: "water", elementType: "labels.text.fill", stylers: [{ color: "#2c3f56" }] },
];

export const tacticalMapStyles = (
  isDark: boolean,
): google.maps.MapTypeStyle[] => (isDark ? TACTICAL_MAP_STYLE_DARK : MAP_STYLE_WHITE);

export const MEXICO_CENTER: LatLngLiteral = { lat: 23.2, lng: -101.5 };

export const worstStatus = (statuses: WatchStatus[]): WatchStatus =>
  statuses.reduce<WatchStatus>(
    (worst, status) =>
      WATCH_STATUS_WEIGHT[status] < WATCH_STATUS_WEIGHT[worst] ? status : worst,
    "safe",
  );

export const byWatchSeverity = (a: WatchStatus, b: WatchStatus): number =>
  WATCH_STATUS_WEIGHT[a] - WATCH_STATUS_WEIGHT[b];

export const geofenceColor = (geofence: Geofence): string => {
  const own = (geofence.color ?? "").trim();
  if (/^#([0-9a-f]{3}|[0-9a-f]{6})$/i.test(own)) return own;
  return GEOFENCE_TYPE_MAP_COLORS[geofence.type] ?? GEOFENCE_TYPE_MAP_COLORS.default;
};

export interface GeofenceCircle {
  kind: "circle";
  center: LatLngLiteral;
  radius: number;
}

export interface GeofencePolygon {
  kind: "polygon";
  path: LatLngLiteral[];
  center: LatLngLiteral;
}

export type GeofenceGeometry = GeofenceCircle | GeofencePolygon;

const isFinitePair = (lat: unknown, lng: unknown): boolean =>
  typeof lat === "number" &&
  typeof lng === "number" &&
  Number.isFinite(lat) &&
  Number.isFinite(lng) &&
  (lat !== 0 || lng !== 0);

export function geofenceGeometry(geofence: Geofence): GeofenceGeometry | null {
  const coordinates = geofence.coordinates;
  if (!coordinates) return null;

  if (!Array.isArray(coordinates)) {
    if (!isFinitePair(coordinates.latitude, coordinates.longitude)) return null;
    return {
      kind: "circle",
      center: { lat: coordinates.latitude, lng: coordinates.longitude },
      radius: Number.isFinite(coordinates.radius) && coordinates.radius > 0
        ? coordinates.radius
        : 250,
    };
  }

  const path = coordinates
    .filter((point) => isFinitePair(point.latitude, point.longitude))
    .map((point) => ({ lat: point.latitude, lng: point.longitude }));

  if (path.length < 3) return null;

  const center = path.reduce(
    (acc, point) => ({ lat: acc.lat + point.lat / path.length, lng: acc.lng + point.lng / path.length }),
    { lat: 0, lng: 0 },
  );

  return { kind: "polygon", path, center };
}

export function routePath(route: Route): LatLngLiteral[] {
  const dense = route.principal_route_dense ?? [];
  if (dense.length >= 2) {
    return dense
      .filter((point) => isFinitePair(point.latitude, point.longitude))
      .map((point) => ({ lat: point.latitude, lng: point.longitude }));
  }
  return (route.principal_route ?? [])
    .filter((point) => isFinitePair(point.latitud, point.longitud))
    .map((point) => ({ lat: point.latitud, lng: point.longitud }));
}

const EARTH_RADIUS_M = 6378137;

export function distanceMeters(a: LatLngLiteral, b: LatLngLiteral): number {
  const toRad = (value: number) => (value * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLng = toRad(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * EARTH_RADIUS_M * Math.asin(Math.min(1, Math.sqrt(h)));
}

export function formatDistance(meters: number): string {
  return meters >= 1000 ? `${(meters / 1000).toFixed(1)} km` : `${Math.round(meters)} m`;
}

const WORLD_SIZE = 256;

function worldPoint(position: LatLngLiteral): { x: number; y: number } {
  const sin = Math.sin((position.lat * Math.PI) / 180);
  const clamped = Math.min(Math.max(sin, -0.9999), 0.9999);
  return {
    x: WORLD_SIZE * (0.5 + position.lng / 360),
    y: WORLD_SIZE * (0.5 - Math.log((1 + clamped) / (1 - clamped)) / (4 * Math.PI)),
  };
}

export interface ClusterInput {
  id: string;
  position: LatLngLiteral;
  status: WatchStatus;
}

export interface MapCluster<T extends ClusterInput> {
  id: string;
  position: LatLngLiteral;
  members: T[];
  status: WatchStatus;
}

export function clusterByPixelGrid<T extends ClusterInput>(
  items: T[],
  zoom: number,
  gridPx: number,
): MapCluster<T>[] {
  const scale = 2 ** zoom;
  const cell = gridPx / scale;
  const buckets = new Map<string, T[]>();

  for (const item of items) {
    const point = worldPoint(item.position);
    const key = `${Math.floor(point.x / cell)}:${Math.floor(point.y / cell)}`;
    const bucket = buckets.get(key);
    if (bucket) bucket.push(item);
    else buckets.set(key, [item]);
  }

  return [...buckets.values()].map((members) => {
    const position = members.reduce(
      (acc, member) => ({
        lat: acc.lat + member.position.lat / members.length,
        lng: acc.lng + member.position.lng / members.length,
      }),
      { lat: 0, lng: 0 },
    );
    return {
      id: members.map((member) => member.id).join("|"),
      position: members.length === 1 ? members[0].position : position,
      members,
      status: worstStatus(members.map((member) => member.status)),
    };
  });
}
